<template>
  <div
    id="content"
    class="flex-column"
  >
    <RBAlertBox v-if="showLoginMessage">
      Identificamos que você já possui vínculo com o <b>Pravaler</b>, experimente fazer o
      <a
        target="_blank"
        href="https://novoportal.pravaler.com.br/auth/login"
      >login no portal</a>
    </RBAlertBox>
    <div
      class="flex-column"
      v-if="studantSituation !== 'new'"
    >
      <p class="title">
        Certo, {{ studentData.name.split(' ')[0] }}
      </p>
      <p class="text">
        Ao continuar, você utiliza seus dados já fornecidos.
      </p>
      <div class="identified flex-column">
        <span class="text">{{ studentData.name }}</span>
        <span class="text">{{ studentData.email }}</span>
        <span class="text">{{ formatPhoneNumber(studentData.phone) }}</span>
        <span class="text">{{ formatCpf(studentData.cpf) }}</span>
      </div>
    </div>
    <div
      class="flex-column"
      v-else
    >
      <p class="title">
        Certo
      </p>
      <p class="text">
        Faltam apenas alguns dados para continuar.
      </p>
    </div>
    <form
      id="rb-form"
      class="flex-column"
      @submit.prevent="submit"
    >
      <RbInput
        v-model="student.name"
        label="Nome do estudante"
        required
        primary
        v-if="visibleField('name')"
      />
      <RbInput
        v-model="student.email"
        label="E-mail"
        type="email"
        required
        primary
        v-if="visibleField('email')"
      />
      <RbPhone
        v-model="student.phone"
        label="Celular"
        required
        primary
        v-if="visibleField('phone')"
      />
      <RbCpf
        v-model="student.cpf"
        label="CPF"
        required
        primary
        v-if="visibleField('cpf')"
      />
      <RbButton
        type="button"
        text="Alterar dados"
        text-color="var(--primary)"
        class="change-data"
        @click="studantSituation = 'new'"
        plain
        v-if="studantSituation !== 'new'"
      />
      <RbCheckbox
        v-model="student.accept_terms"
        name="privacy"
        primary
        required
      >
        Concordo com Termos de uso e Políticas de privacidade.
      </RbCheckbox>
      <RbCheckbox
        v-model="student.accept_communication"
        label="Aceito receber comunicação por WhatsApp."
        name="whatsapp"
        primary
      />
    </form>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, reactive, ref } from 'vue';
import { getPerson } from '@/api/Person';
import { studentStep } from '@/api/Step';
import { handleStudentSession } from '@/lib/Auth';
import { loaderStore, stepStore, proposalStore, snackbarStore, appStore } from '@/store/app';
import { formatCpf, formatPhoneNumber } from '@/lib/Formatting';
import { Tracking } from '@/lib/Tracking';
import type { IStudent } from './interface/IStudent';
import RbInput from '../standards/RbInput.vue';
import RbCheckbox from '../standards/RbCheckbox.vue';
import RbButton from '../standards/RbButton.vue';
import RbCpf from '../standards/RbCpf.vue';
import RbPhone from '../standards/RbPhone.vue';
import RBAlertBox from '../standards/RBAlertBox.vue';

onBeforeMount(async () => {
  try {
    loaderStore().setOpen(true);
    const person = await getPerson();
    const trackingData = !appStore().global.ignoreTracking ? await getTrackingData() : false;

    studentData = handleStudentData(trackingData, person);

    Object.freeze(studentData);
    Object.assign(student, studentData);
    
    studantSituation.value = getStudantSituation();
  } catch (error) {
    snackbarStore().alert('Tivemos um problema ao buscar seus dados!');
  } finally {
    loaderStore().setOpen(false);
  }
});

function handleStudentData(tracking, person) {
  if(!tracking || !Boolean(tracking.cpf)) {
    return person;
  }

  if(tracking.cpf == person.cpf) {
    return {
      ...person,
      name: tracking.nome ?? person.name,
      email: tracking.emailPrincipal ?? person.email,
      cpf: tracking.cpf ?? person.cpf,
      phone: tracking.telefonePrincipal ?? person.phone
    };
  }

  return {
    name: tracking.nome,
    email: tracking.emailPrincipal,
    cpf: tracking.cpf,
    phone: tracking.telefonePrincipal,
    accept_communication: false,
    accept_terms: false
  } 
}

async function getTrackingData() {
  return (await Tracking.getData() as any).data;
}

const studantSituation = ref<'completed' | 'parcial' | 'new'>('new');
const showLoginMessage = ref(false);
let studentData;
const student = reactive<IStudent>({
  name: '',
  email: '',
  cpf: '',
  phone: '',
  accept_communication: false,
  accept_terms: false
});

function getStudantSituation() {
  if (student.id && student.name && student.email && student.phone && student.cpf) {
    return 'completed';
  }
  if (student.name || student.email || student.phone || student.cpf) {
    return 'parcial';
  }
  return 'new';
}

function visibleField(field): boolean {
  if (studantSituation.value === 'new') {
    return true;
  }

  if (studantSituation.value === 'parcial' && studentData && !studentData[field]) {
    return true;
  }

  return false;
}

async function submit() {
  loaderStore().setOpen(true);
  await handleStudentSession(student.cpf.replace(/[^0-9\\]+/g, ''));
  const response = await studentStep(getSubmitData());
  if (!response) {
    loaderStore().setOpen(false);
    return snackbarStore().alert('Tivemos um problema para salvar seus dados, tente novamente!');
  }

  showLoginMessage.value = Boolean(response.redirect_pravaler);

  if (showLoginMessage.value) return loaderStore().setOpen(false);

  if (response.proposal) {
    proposalStore().setProposal(response.proposal);
    appStore().setGlobal({...appStore().global, ignoreTracking: true})
    loaderStore().setOpen(false);
    return stepStore().nextStep('complementary');
  }
  snackbarStore().alert('Tivemos um problema para salvar seus dados, tente novamente!');
  loaderStore().setOpen(false);
}

function getSubmitData() {
  return {
    student: {
      name: student.name,
      email: student.email,
      cpf: student.cpf,
      phone: student.phone,
      accept_communication: student.accept_communication,
      accept_terms: student.accept_terms,
    },
    proposal: proposalStore().getProposal()
  };
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}

#content {
  margin: 1.3rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .title {
    font-family: Poppins;
    font-size: 1rem;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0em;
    text-align: left;
  }

  .text {
    font-size: 0.90rem;
    font-weight: 400;
    line-height: 1.45rem;
    letter-spacing: 0.025em;
    text-align: left;
    color: rgba(77, 77, 77, 1);
  }

  .change-data {
    width: fit-content;
  }

  &>div {
    gap: 16px;

    &>div.identified {
      &>span {
        line-height: 16px;
        margin: 2px;
        color: rgba(128, 128, 128, 1);
      }
    }
  }

  &>form {
    gap: 25px;
  }
}
</style>
